import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';
import { ScopeLevelSchema } from './permissions.js';

/**
 * The principal model (doc 06 section 1).
 *
 * Everything that can make a request resolves to one of these. Authorisation is
 * always can(principal, action, resource); there is no other entry point.
 */

export const PrincipalSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('user'),
    userId: IdSchema,
    sessionId: IdSchema,
    mfaAt: z.date().nullable(),
    impersonatedBy: IdSchema.optional(),
  }),
  z.object({
    kind: z.literal('service'),
    serviceAccountId: IdSchema,
    credentialId: IdSchema,
    scopes: z.array(z.string()),
  }),
  z.object({ kind: z.literal('node'), nodeId: IdSchema }),
  z.object({ kind: z.literal('system'), job: z.string() }),
]);
export type Principal = z.infer<typeof PrincipalSchema>;

/**
 * The resolved authorisation scope for a request (doc 06 section 4.1 step 3).
 *
 * `platform` means every tenant. Otherwise `tenantIds` is the exhaustive set the
 * principal may address -- the repository layer constrains every query by it, and
 * RLS constrains the same query independently (ADR-002 layer 1).
 */
export interface ResolvedScope {
  level: 'platform' | 'reseller' | 'tenant' | 'site';
  resellerIds: string[];
  tenantIds: string[];
  siteIds: string[];
  /** True only for platform scope, where tenantIds is not enumerated. */
  allTenants: boolean;
}

// ------------------------------------------------------------------- DTOs

export const UserSchema = z.object({
  id: IdSchema,
  email: z.string().email(),
  full_name: z.string().nullable(),
  avatar_url: z.string().nullable(),
  status: z.enum(['active', 'disabled', 'locked']),
  timezone: z.string().nullable(),
  locale: z.string().nullable(),
  mfa_enforced: z.boolean(),
  last_seen_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type User = z.infer<typeof UserSchema>;

export const MembershipSchema = z.object({
  id: IdSchema,
  role_key: z.string(),
  role_name: z.string(),
  scope_level: ScopeLevelSchema,
  reseller_id: IdSchema.nullable(),
  tenant_id: IdSchema.nullable(),
  tenant_name: z.string().nullable(),
  site_id: IdSchema.nullable(),
});
export type Membership = z.infer<typeof MembershipSchema>;

/**
 * Session bootstrap -- everything the SPA needs before first paint
 * (doc 07 section 1). The UI renders from this; the server enforces regardless.
 */
export const SessionBootstrapSchema = z.object({
  user: UserSchema,
  memberships: z.array(MembershipSchema),
  permissions: z.array(z.string()),
  scope: z.object({
    level: ScopeLevelSchema,
    all_tenants: z.boolean(),
    tenant_ids: z.array(IdSchema),
    reseller_ids: z.array(IdSchema),
  }),
  active_tenant_id: IdSchema.nullable(),
  impersonated_by: z.string().nullable(),
  permissions_version: z.number().int(),
});
export type SessionBootstrap = z.infer<typeof SessionBootstrapSchema>;

// ------------------------------------------------------------------ requests

export const LoginRequestSchema = z.object({
  email: z.string().email().max(320),
  password: z.string().min(1).max(512),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const LoginResponseSchema = z.union([
  z.object({
    status: z.literal('ok'),
    access_token: z.string(),
    expires_in: z.number().int(),
    user: UserSchema,
  }),
  z.object({
    status: z.literal('mfa_required'),
    mfa_ticket: z.string(),
    expires_in: z.number().int(),
  }),
  z.object({
    status: z.literal('sso_required'),
    connection_id: IdSchema,
    redirect_url: z.string(),
  }),
]);
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

export const RefreshResponseSchema = z.object({
  access_token: z.string(),
  expires_in: z.number().int(),
});
export type RefreshResponse = z.infer<typeof RefreshResponseSchema>;
